import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaBars, FaTimes } from "react-icons/fa";
import RegistrationModal from "../auth/RegistrationModal";
import {
  Bell,
  Briefcase,
  Heart,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Settings,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getProfile as getPlannerPortfolio } from "../../api/portfolio";
import UserAvatar from "./UserAvatar";
import { isCompletedProfessional, isPendingProfessional } from "../../utils/authUtils";

const gplogo =
  "https://res.cloudinary.com/de6aivvo3/image/upload/e_trim/v1748882192/Logo_cjqrdz.svg";

function Navbar() {
  const navigate = useNavigate();
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [isSubmissionSuccessOpen, setIsSubmissionSuccessOpen] = useState(false);
  const [eventFormConfig, setEventFormConfig] = useState({
    mode: "create",
    initialData: null,
  });
  const location = useLocation();
  const authState = useSelector((state) => state.auth);
  const profileState = useSelector((state) => state.profile);
  const drawerHistoryPushed = useRef(false);
  const profileMenuRef = useRef(null);

  const openRegistrationModal = (step) => {
    setModalStep(step);
    setIsRegistrationModalOpen(true);
  };

  // Auto-popup registration modal instantly on site visit for incomplete professionals
  useEffect(() => {
    if (isPendingProfessional()) {
      const draftStep = parseInt(localStorage.getItem("draftStep"), 10) || 2;
      openRegistrationModal(draftStep);
    }
  }, []);

  // Allow global triggering of registration modal via custom event
  useEffect(() => {
    const handleOpenRegistration = (e) => {
      const step = e.detail?.step || parseInt(localStorage.getItem("draftStep"), 10) || 2;
      openRegistrationModal(step);
    };

    window.addEventListener("openRegistrationModal", handleOpenRegistration);
    return () => window.removeEventListener("openRegistrationModal", handleOpenRegistration);
  }, []);

  // Simple close for drawer when navigating via a menu item
  const closeDrawer = () => {
    setIsDrawerOpen(false);
    drawerHistoryPushed.current = false;
  };
  const closeEventFormModal = () => setIsNewModalOpen(false);

  const handleEventFormSubmitSuccess = () => {
    setIsNewModalOpen(false);
    setIsSubmissionSuccessOpen(true);
  };

  const closeSubmissionSuccessModal = () => setIsSubmissionSuccessOpen(false);

  const closeRegistrationModal = () => setIsRegistrationModalOpen(false);

  const toggleDrawer = () => {
    // Open drawer: push a history state so hardware back closes it first
    if (!isDrawerOpen) {
      try {
        window.history.pushState({ drawer: true }, "");
        drawerHistoryPushed.current = true;
        // Ensure dashboard sidebar closes when navbar drawer opens
        try {
          window.dispatchEvent(new Event("closeDashboardSidebar"));
        } catch (e) {
          // ignore
        }
      } catch (e) {
        // no-op if history not available
      }
      setIsDrawerOpen(true);
    } else {
      // Close drawer: consume the extra history entry so back behaves normally
      setIsDrawerOpen(false);
      // Only go back if the current history entry is our drawer marker.
      const isDrawerState = !!(
        window.history &&
        window.history.state &&
        window.history.state.drawer
      );
      if (drawerHistoryPushed.current && isDrawerState) {
        drawerHistoryPushed.current = false;
        try {
          window.history.back();
        } catch (e) {
          // ignore
        }
      } else {
        drawerHistoryPushed.current = false;
      }
    }
  };

  // Close the drawer on browser back button before navigating away (mobile behavior)
  useEffect(() => {
    const onPopState = () => {
      if (isDrawerOpen) {
        setIsDrawerOpen(false);
        drawerHistoryPushed.current = false;
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [isDrawerOpen]);

  // Close the profile menu when the route changes or the user clicks outside it.
  useEffect(() => {
    setIsProfileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target)
      ) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // Listen for dashboard sidebar open requests and close navbar drawer if open
  useEffect(() => {
    const onExternalClose = () => {
      if (!isDrawerOpen) return;
      setIsDrawerOpen(false);
      // If we pushed a history entry for the drawer, revert it so back behaves normally
      const isDrawerState = !!(
        window.history &&
        window.history.state &&
        window.history.state.drawer
      );
      if (drawerHistoryPushed.current && isDrawerState) {
        drawerHistoryPushed.current = false;
        try {
          window.history.back();
        } catch (e) {
          // ignore
        }
      } else {
        drawerHistoryPushed.current = false;
      }
    };
    window.addEventListener("closeNavbarDrawer", onExternalClose);
    return () =>
      window.removeEventListener("closeNavbarDrawer", onExternalClose);
  }, [isDrawerOpen]);

  // Prevent body scroll while drawer is open (mobile UX)
  useEffect(() => {
    const original = document.body.style.overflow;
    if (isDrawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = original || "";
    }
    return () => {
      document.body.style.overflow = original || "";
    };
  }, [isDrawerOpen]);

  const logout = () => {
    // Clear all localStorage items
    localStorage.clear();
    // Clear CometChat initialization flag
    localStorage.removeItem("chatInitialized");
    // Force a page refresh
    window.location.href = "/";
  };

  // Get user details from localStorage, preferring companyName over fullName
  const getUserFromStorage = () => ({
    name:
      profileState?.profileData?.portfolio?.companyName ||
      localStorage.getItem("companyName") ||
      localStorage.getItem("fullName") ||
      localStorage.getItem("name") ||
      "",
    role: localStorage.getItem("role") || "",
    professionalRole:
      profileState?.profileData?.professional?.role ||
      localStorage.getItem("professionalRole") ||
      "",
    profilePicture:
      profileState?.profileData?.portfolio?.profilePicture?.url ||
      localStorage.getItem("profilePicture") ||
      "",
  });
  const [user, setUser] = useState(getUserFromStorage());

  const isProfessional = user.role === "professional";
  const onboardingComplete = isProfessional ? isCompletedProfessional() : true;

  let profileMenuItems = isProfessional
    ? [
      {
        label: "Dashboard",
        action: onboardingComplete ? undefined : () => openRegistrationModal(parseInt(localStorage.getItem("draftStep")) || 2),
        path: onboardingComplete ? "/dashboard/professional" : undefined,
        icon: <LayoutDashboard className="w-4 h-4" />,
      },
      {
        label: "My Bookings",
        action: onboardingComplete ? undefined : () => openRegistrationModal(parseInt(localStorage.getItem("draftStep")) || 2),
        path: onboardingComplete ? "/dashboard/professional/bookings" : undefined,
        icon: <Briefcase className="w-4 h-4" />,
      },
      {
        label: "Settings",
        action: onboardingComplete ? undefined : () => openRegistrationModal(parseInt(localStorage.getItem("draftStep")) || 2),
        path: onboardingComplete ? "/dashboard/professional/settings" : undefined,
        icon: <Settings className="w-4 h-4" />,
      },
    ]
    : [
      {
        label: "My events",
        path: "/dashboard/customer",
        icon: <LayoutDashboard className="w-4 h-4" />,
      },
      { label: "My Booking", path: "/my-booking", icon: <Briefcase className="w-4 h-4" /> },
      {
        label: "Settings",
        path: "/customer/profile",
        icon: <Settings className="w-4 h-4" />,
      },
    ];

  useEffect(() => {
    setUser(getUserFromStorage());
  }, [authState, profileState]);
  useEffect(() => {
    const refreshUser = () => setUser(getUserFromStorage());

    window.addEventListener("focus", refreshUser);

    return () => window.removeEventListener("focus", refreshUser);
  }, []);

  // Listen for cross-tab storage changes and in-app profile updates
  useEffect(() => {
    const refresh = () => setUser(getUserFromStorage());
    const onStorage = (e) => {
      if (
        [
          "companyName",
          "fullName",
          "profilePicture",
          "role",
          "professionalRole",
        ].includes(e.key)
      ) {
        refresh();
      }
    };
    const onProfileUpdated = (e) => {
      // Re-read all user fields from both event detail and localStorage immediately
      // so the navbar updates without a page refresh after login/register.
      const detail = e.detail || {};
      setUser((prev) => ({
        ...prev,
        name:
          detail.companyName ??
          detail.fullName ??
          localStorage.getItem("companyName") ??
          localStorage.getItem("fullName") ??
          localStorage.getItem("name") ??
          prev.name,
        role: localStorage.getItem("role") || prev.role,
        professionalRole:
          localStorage.getItem("professionalRole") || prev.professionalRole,
        profilePicture:
          detail.profilePictureUrl ??
          localStorage.getItem("profilePicture") ??
          prev.profilePicture,
      }));
    };
    const onOpenEventForm = (e) => {
      const { mode = "edit", initialData = null } = e.detail || {};
      setEventFormConfig({ mode, initialData });
      setIsSubmissionSuccessOpen(false);
      setIsNewModalOpen(true);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("profile:updated", onProfileUpdated);
    window.addEventListener("openEventForm", onOpenEventForm);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("profile:updated", onProfileUpdated);
      window.removeEventListener("openEventForm", onOpenEventForm);
    };
  }, []);

  // On mount, if professional and companyName missing, fetch portfolio to populate navbar immediately
  useEffect(() => {
    const role = localStorage.getItem("role");
    const hasCompany = !!localStorage.getItem("companyName");
    if (role === "professional" && !hasCompany) {
      (async () => {
        try {
          const res = await getPlannerPortfolio(); // { user, portfolio }
          const pf = res?.portfolio || {};
          const companyName = pf?.companyName || "";
          const pictureUrl = pf?.profilePicture?.url || "";
          if (companyName) localStorage.setItem("companyName", companyName);
          if (pictureUrl) localStorage.setItem("profilePicture", pictureUrl);
          // Notify components to update immediately
          window.dispatchEvent(
            new CustomEvent("profile:updated", {
              detail: { companyName, profilePictureUrl: pictureUrl },
            }),
          );
        } catch (e) {
          // silently ignore; navbar will still show fullName fallback
        }
      })();
    }
  }, []);

  //if the use is on a dashboard route and is a professional, we hide the profile menu because they should use the dashboard sidebar instead. This is to avoid confusion and redundancy in navigation options.
  const isAtDashboardRoute = location.pathname.startsWith("/dashboard");
  if (isProfessional && isAtDashboardRoute) {
    profileMenuItems = [];
  }

  const isLoggedIn =
    !!localStorage.getItem("authToken") || !!localStorage.getItem("token");

  // Updated: outlined icon style (white bg + orange border/icon).
  const navIconButtonClass =
    "relative inline-flex items-center justify-center w-10 h-10 rounded-[10px] border border-[#FF5A3C] bg-white text-[#FF5A3C] hover:bg-[#FFF1ED] transition";

  // Updated: top-right badge for activity indicators.
  const navIconBadgeClass =
    "absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#FF4C18] border-2 border-white";

  return (
    <nav className="fixed top-0 left-0 right-0 shadow-md w-full flex items-center justify-between px-4 md:px-6 bg-white font-medium shadow-[#F4F4F4] z-50">
      <div className="flex items-center gap-0">
        <div className="flex items-center">
          <button
            onClick={() => {
              if (user?.role === "professional" && onboardingComplete) {
                navigate("/dashboard/professional");
              } else {
                navigate("/");
              }
            }}
            className="flex items-center justify-center gap-3 -ml-6 md:-ml-12"
          >
            <img
              className="w-40 md:w-60 h-auto"
              src={gplogo}
              alt="Gopratle logo"
            />
          </button>
        </div>
      </div>
      {/* Mobile: Login + Join buttons */}
      <div className="md:hidden flex items-center gap-2">
        {!isProfessional && (
          <button
            onClick={() => {
              window.dispatchEvent(new CustomEvent("openEventWizard"));
            }}
            className="bg-[#FF4C18] text-white text-[12px] font-medium border border-[#FF4C18] hover:bg-white hover:text-[#FF4C18] px-3 py-1.5 rounded-md transition-colors whitespace-nowrap"
          >
            Post my Event
          </button>
        )}

        {!isLoggedIn && (
          <>
            <button
              onClick={() => openRegistrationModal("login")}
              className="text-[#FF4C18] text-[12px] font-medium border border-[#FF4C18] hover:bg-[#FF4C18] hover:text-white px-3 py-1.5 rounded-md transition-colors"
            >
              Login
            </button>
            {/* Hiding Join on mobile if Plan my Event is shown to save space, but kept it if they want. Actually let's keep Join but use shorter padding */}
            <button
              onClick={() => openRegistrationModal(1)}
              className="bg-[#FF4C18] text-white text-[12px] font-medium border border-[#FF4C18] hover:bg-white hover:text-[#FF4C18] px-3 py-1.5 rounded-md transition-colors"
            >
              Join
            </button>
          </>
        )}

        {isLoggedIn && (
          <button
            onClick={toggleDrawer}
            className="text-gray-500 focus:outline-none ml-1"
          >
            {isDrawerOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        )}
      </div>

      {/* Drawer for Mobile and Tablet */}
      <div
        className={`fixed top-0 right-0 h-full w-3/4 bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50 md:hidden ${isDrawerOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        <div className="flex items-center justify-between px-3 py-3">
          <button
            onClick={() => { closeDrawer(); navigate(user?.role === 'professional' && onboardingComplete ? '/dashboard/professional' : '/'); }}
            className="overflow-hidden h-10 w-28 flex items-center"
          >
            <img
              src={gplogo}
              alt="Gopratle logo"
              className="h-full w-auto object-cover object-left"
              style={{ maxWidth: '120px', objectPosition: 'left center' }}
            />
          </button>
          <button
            onClick={toggleDrawer}
            className="text-gray-500 focus:outline-none"
          >
            <FaTimes size={24} />
          </button>
        </div>
        <div className="flex flex-col w-full items-start gap-7 px-6 py-6 overflow-y-auto max-h-[calc(100vh-64px)]">
          {isLoggedIn && (
            <div className="w-full pb-4 mb-4 border-b border-gray-200">
              {/* Static profile header for mobile drawer (no dropdown) */}
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <UserAvatar
                    name={user?.name}
                    profileImage={user?.profilePicture}
                    className="w-10 h-10"
                  />
                  <div className="flex flex-col">
                    <span
                      className={`${(user?.name || "").length > 18 ? "text-[13px]" : "text-[15px]"} font-semibold text-black leading-5`}
                    >
                      {user?.name || "User"}
                    </span>
                    {user?.role && (
                      <span className="text-[13px] text-gray-500 capitalize">
                        {user.role === "professional"
                          ? user.professionalRole || "Professional"
                          : user.role === "customer"
                            ? "Host"
                            : user.role}
                      </span>
                    )}
                  </div>
                </div>
                {/* removed dropdown chevron on mobile */}
              </div>
            </div>
          )}
          {!(isLoggedIn && user.role === "professional") && (
            <>
              {/* Mobile: Packages temporarily hidden
              <Link
                to="/packages"
                onClick={closeDrawer}
                className="flex justify-between items-center border-b-2 w-full hover:text-[#FF4C18]"
              >
                Packages
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m8.25 4.5 7.5 7.5-7.5 7.5"
                  />
                </svg>
              </Link>
              */}
            </>
          )}

          {isLoggedIn ? (
            <>
              {isProfessional ? (
                <>
                  {[
                    {
                      label: "Dashboard",
                      path: "/dashboard/professional",
                    },
                    {
                      label: "My Portfolio",
                      path: "/dashboard/professional/portfolio",
                    },
                    {
                      label: "My Bookings",
                      path: "/dashboard/professional/bookings",
                    },
                    {
                      label: "Help",
                      path: "/help",
                    },
                    {
                      label: "Settings",
                      path: "/dashboard/professional/settings",
                    },
                  ].map(({ label, path }) => (
                    <button
                      key={label}
                      onClick={() => {
                        if (path === "/help") {
                          navigate(path);
                        } else if (onboardingComplete) {
                          navigate(path);
                        } else {
                          openRegistrationModal(
                            parseInt(localStorage.getItem("draftStep")) || 2
                          );
                        }
                        closeDrawer();
                      }}
                      className="flex justify-between items-center border-b-2 w-full hover:text-[#FF4C18] py-2"
                    >
                      {label}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="size-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m8.25 4.5 7.5 7.5-7.5 7.5"
                        />
                      </svg>
                    </button>
                  ))}

                  <button
                    onClick={() => {
                      logout();
                      closeDrawer();
                    }}
                    className="text-[#FF4C18] text-[12px] font-medium border border-[#FF4C18] hover:bg-[#FF4C18] hover:text-white hover:cursor-pointer px-6 py-2 w-full rounded-md transition-colors"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { navigate("/dashboard/customer"); closeDrawer(); }}
                    className="flex justify-between items-center border-b-2 w-full hover:text-[#FF4C18]"
                  >
                    My events
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                    </svg>
                  </button>
                  <button
                    onClick={() => { navigate("/my-booking"); closeDrawer(); }}
                    className="flex justify-between items-center border-b-2 w-full hover:text-[#FF4C18]"
                  >
                    My Booking
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      const custId = localStorage.getItem("customerId");
                      if (custId) navigate(`/list/${custId}/planner`);
                      closeDrawer();
                    }}
                    className="flex justify-between items-center border-b-2 w-full hover:text-[#FF4C18]"
                  >
                    Favourites
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      navigate("/customer/profile");
                      closeDrawer();
                    }}
                    className="flex justify-between items-center border-b-2 w-full hover:text-[#FF4C18]"
                  >
                    Settings
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="size-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8.25 4.5 7.5 7.5-7.5 7.5"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      logout();
                      closeDrawer();
                    }}
                    className="text-[#FF4C18] text-[12px] font-medium border border-[#FF4C18] hover:bg-[#FF4C18] hover:text-white hover:cursor-pointer px-6 py-2 w-full rounded-md transition-colors"
                  >
                    Logout
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              <Link
                to="/organizers"
                onClick={closeDrawer}
                className="flex justify-between items-center border-b-2 w-full hover:text-[#FF4C18]"
              >
                Find Planners
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m8.25 4.5 7.5 7.5-7.5 7.5"
                  />
                </svg>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Desktop Actions */}
      <div className="hidden md:flex items-center gap-5">
        {isLoggedIn ? (
          <div className="flex items-center gap-2">
            {/* Chat — temporarily hidden
            <button
              onClick={() => navigate("/chat")}
              title="Chat"
              aria-label="Chat"
              className={navIconButtonClass}
            >
              <MessageSquare className="w-5 h-5" />
              <span className={navIconBadgeClass} />
            </button>
            */}

            {user?.role === "customer" && (
              <button
                onClick={() => {
                  const custId = localStorage.getItem("customerId");
                  if (custId) navigate(`/list/${custId}/planner`);
                }}
                title="Favourites"
                aria-label="Favourites"
                className={navIconButtonClass}
              >
                <Heart className="w-5 h-5" />
              </button>
            )}

            {/* Notifications — temporarily hidden
            <button
              onClick={() => navigate("/notifications")}
              title="Notifications"
              aria-label="Notifications"
              className={navIconButtonClass}
            >
              <Bell className="w-5 h-5" />
              <span className={navIconBadgeClass} />
            </button>
            */}
            {!isProfessional && (
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("openEventWizard"));
                }}
                className="hover:bg-white bg-[#FF4C18] hover:text-[#FF4C18] text-white text-[12px] font-medium border border-[#FF4C18] hover:cursor-pointer px-6 py-2 rounded-md transition-colors"
              >
                Post my Event
              </button>
            )}

            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                title="Profile"
                aria-label="Profile menu"
                className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-gray-200 hover:border-[#FF4C18] hover:bg-orange-50 transition overflow-hidden"
              >
                <UserAvatar
                  name={user?.name}
                  profileImage={user?.profilePicture}
                  className="w-12 h-12"
                />
              </button>

              {isProfileMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg z-50">
                  <div className="py-1">
                    {profileMenuItems.map((item) => (
                      item.action ? (
                        <button
                          key={item.label}
                          onClick={() => {
                            setIsProfileMenuOpen(false);
                            item.action();
                          }}
                          className="flex w-full items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-[#FF4C18]/10 hover:text-[#FF4C18]"
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </button>
                      ) : (
                        <Link
                          key={item.label}
                          to={item.path}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-[#FF4C18]/10 hover:text-[#FF4C18]"
                          onClick={() => setIsProfileMenuOpen(false)}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </Link>
                      )
                    ))}
                  </div>

                  <div className="border-t border-gray-100" />

                  <button
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      logout();
                    }}
                    className="flex w-full items-center gap-3 px-4 py-3 text-sm font-medium text-red-500 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {!isProfessional && (
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("openEventWizard"));
                }}
                className="hover:bg-white bg-[#FF4C18] hover:text-[#FF4C18] text-white text-[12px] font-medium border border-[#FF4C18] hover:cursor-pointer px-6 py-2 rounded-md transition-colors"
              >
                Post my Event
              </button>
            )}
            <button
              onClick={() => openRegistrationModal("login")}
              className="text-[#FF4C18] text-[12px] font-medium border border-[#FF4C18] hover:bg-[#FF4C18] hover:text-white hover:cursor-pointer px-6 py-2 rounded-md transition-colors"
            >
              Login
            </button>

            <button
              onClick={() => openRegistrationModal(1)}
              className="text-[#FF4C18] text-[12px] font-medium border border-[#FF4C18] hover:bg-[#FF4C18] hover:text-white hover:cursor-pointer px-6 py-2 rounded-md transition-colors"
            >
              Join
            </button>
          </>
        )}
      </div>

      {/* Modals */}
      {isRegistrationModalOpen && (
        <RegistrationModal
          onClose={closeRegistrationModal}
          initialStep={modalStep}
        />
      )}


    </nav>
  );
}

export default Navbar;
